// src/components/CommonComponents/RowActions.jsx
import React, { useEffect, useRef, useState } from "react";
import { MoreVertical } from "lucide-react";

/**
 * RowActions (확장형 ⋮ 메뉴)
 *
 * props
 *  - items: Array<{
 *      key: string;
 *      label: React.ReactNode;
 *      onClick?: () => void;
 *      disabled?: boolean;
 *      danger?: boolean;        // 빨간색 강조
 *      icon?: React.ReactNode;  // 왼쪽 아이콘
 *      hidden?: boolean;        // 조건부 숨김
 *      dividerAbove?: boolean;  // 항목 위 구분선
 *    }>
 *  - align?: 'left' | 'right'  // 메뉴 정렬(기본 right)
 *  - buttonClassName?: string   // ⋮ 버튼 추가 클래스
 *  - onOpenChange?: (open: boolean) => void
 */
export default function RowActions({
  items = [],
  align = "right",
  buttonClassName = "",
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // 열린 상태 변화를 외부에 알림(옵션)
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  // 바깥 클릭 / ESC 닫기
  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClickOutside);
      document.addEventListener("keydown", onKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const visibleItems = items.filter((it) => !it.hidden);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          "inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-gray-100 " +
          buttonClassName
        }
        onClick={(e) => {
          e.stopPropagation();          // ✅ 행 onClick 전파 차단
          setOpen((v) => !v);
        }}
      >
        <MoreVertical className="h-4 w-4 text-gray-600" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="row actions"
          className={`absolute z-[100] mt-2 min-w-30 rounded-xl border border-gray-200 bg-white shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          }`}
          onClick={(e) => e.stopPropagation()} // ✅ 내부 클릭도 전파 차단
        >
          {visibleItems.length === 0 ? (
            <div className="px-4 py-2 text-sm text-gray-400">항목 없음</div>
          ) : (
            visibleItems.map((it, i) => (
              <div key={it.key || i}>
                {it.dividerAbove && <div className="my-1 border-t border-gray-100" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={it.disabled}
                  className={
                    "flex w-full items-center gap-2 px-4 py-2 text-left text-sm " +
                    (it.disabled
                      ? "text-gray-300 cursor-not-allowed"
                      : it.danger
                      ? "text-black hover:bg-[#F2F0FD] hover:text-[#7864F9]"
                      : "text-black hover:bg-[#F2F0FD] hover:text-[#7864F9]")
                  }
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ 행 onClick 차단
                    if (it.disabled) return;
                    setOpen(false);
                    it.onClick?.();
                  }}
                >
                  {it.icon ? <span className="shrink-0">{it.icon}</span> : null}
                  <span className="truncate">{it.label}</span>
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
